/**
 * This class provides the `uploadFile` method to upload file to S3 with an
 * authorization provided by a request to an authorization URL. The fetch
 * options is a callable with the interface of `fetch` used to run the request
 * on the authorization backend.
 *
 * The request to the S3 host is done with the global fetch instance.
 */

import invariant from "invariant";

export default class UploadRequest {
    constructor(authorizationURL, options={}) {
        this._authorizationURL = authorizationURL;
        this.fetch = options.fetch || global.fetch.bind(global);
        this.onAuthorization = options.onAuthorization;
    }

    getAuthorizationURL(name) {
        if (!name) {
            return this._authorizationURL;
        }
        const separator = this._authorizationURL.indexOf('?') === -1 ? '?' : '&';
        name = encodeURIComponent(name);
        return `${this._authorizationURL}${separator}name=${name}`;
    }

    async _fetchUploadConfig(name) {
        // Request an Upload authorization
        // https://boto3.amazonaws.com/v1/documentation/api/latest/reference/services/s3.html
        const authorizationURL = this.getAuthorizationURL(name);
        const response = await this.fetch(authorizationURL, {
            method: "POST",
            data: { name }
        });
        invariant(response, 'fetch callback returned an empty value');
        invariant(typeof response.ok === 'boolean', 'fetch callback returned an object that is not a response');
        if (!response.ok) {
            throw new Error(`request on ${this.authorizationURL} failed with ${response.status}`);
        }
        return response.json();
    }

    async uploadFile(file) {
        const { url, fields, ...rest } = await this._fetchUploadConfig(file.name);
        invariant(url, 'url should have been returned by the fetch call');
        invariant(fields, 'fields should have been returned by the fetch call');

        if(this.onAuthorization){
            this.onAuthorization(rest);
        }

        const body = new FormData();
        Object.keys(fields).forEach(key => body.append(key, fields[key]));
        body.append("file", file);

        await this.fetch(url, {
            method: "POST",
            body,
            noAuth: true // we dont want an Auth token in the headers for this request
        });

        const { key } = fields;
        return key;
    }
}
